import { ChangeDetectorRef, Directive, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { GestionclientesService } from 'src/app/clientes/gestionclientes.service';
import { Cliente } from 'src/app/shared/model/ajustesUsuario/cliente.model';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Proyecto } from 'src/app/shared/model/gestion-proyectos/proyecto';
import { EstatusProyecto } from 'src/app/shared/model/proceso/estadoProyecto';
import { OperativoService } from '../../opeartivo.service';

/** Textos y opciones de la plantilla compartida que cambian entre las pantallas de oportunidades. */
export interface VistaOportunidades {
  /** Modificador de la clase oportunidades-page de la pantalla (odp, marketing o desarrolladores). */
  variante: string;
  titulo: string;
  descripcion: string;
  /** Texto bajo "No se encontraron oportunidades" cuando la consulta no trae registros. */
  textoSinResultados: string;
  /** Muestra el botón "Alta de Oportunidad" en el encabezado. */
  conAlta: boolean;
  /** Muestra el filtro "Estado" y lo envía como param3 de la consulta. */
  conFiltroEstatus: boolean;
}

/** Opción del filtro "Estado": name es la clave que viaja al backend y val el texto que se ve. */
export interface OpcionEstatus {
  name: string;
  val: string;
}

/**
 * Lógica común de las tablas de oportunidades (ODP, Marketing y líder de desarrollo): filtros por
 * nombre, cliente y estado, paginación del lado del servidor, semáforo por fecha de estimación y
 * acceso al seguimiento. Cada pantalla define su vista, su consulta y la ruta de su seguimiento.
 */
@Directive()
export abstract class OportunidadesListaBase implements OnInit {
  abstract readonly vista: VistaOportunidades;
  protected abstract readonly rutaSeguimiento: string;

  private readonly router = inject(Router);
  private readonly clientService = inject(GestionclientesService);
  protected readonly service = inject(OperativoService);
  private readonly cdr = inject(ChangeDetectorRef);

  oportunidades: Proyecto[] = [];
  listaClientes: Cliente[] = [];
  listaEstatus: OpcionEstatus[] = [];
  formCliente = new FormControl('');
  requestCliente = new ConsultaList<Cliente>();
  filtro = new ConsultaList<Proyecto>();

  nomProyecto = '';
  nomCliente = '';
  estatusOportunidad = '';
  totalRegistros = 0;
  totalPages = 0;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;

  /** Consulta paginada de la pantalla (cada una llama a su propio servicio del backend). */
  protected abstract consultaOportunidades(filtro: ConsultaList<Proyecto>): Observable<any>;

  /** Se llama con cada página recibida, antes de revisar si hay que regresar a la última página. */
  protected alCargarPagina(): void {
    // Sin acciones extra por omisión; Marketing avisa aquí de las oportunidades vencidas o por vencer.
  }

  /** Alta de oportunidad desde el encabezado; sólo Marketing muestra el botón (vista.conAlta). */
  openAddProyecto(): void {
    // Las pantallas sin alta no hacen nada: su plantilla no pinta el botón.
  }

  ngOnInit(): void {
    this.requestCliente.regInicial = 0;
    this.requestCliente.regXPagina = 1000;
    this.consultarClientes();
    this.formCliente.valueChanges.subscribe(() => {
      this.filtraCliente();
    });
    this.consultaPagina(1);
  }

  consultaPagina(page: number): void {
    this.currentPage = page;
    this.loading = this.oportunidades.length === 0;
    this.filtro.param1 = this.nomProyecto?.trim() || null;
    this.filtro.param2 = this.nomCliente || null;
    if (this.vista.conFiltroEstatus) {
      this.filtro.param3 = this.estatusOportunidad || null;
    }
    this.filtro.regInicial = (page - 1) * this.pageSize;
    this.filtro.regXPagina = this.pageSize;
    this.filtro.pagina = page - 1;
    this.cdr.detectChanges();

    this.consultaOportunidades(this.filtro)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          const content = Array.isArray(response?.content) ? response.content : [];
          this.totalRegistros = Number(response?.totalElements ?? 0);
          this.oportunidades = this.decorateProjects(content);
          this.totalPages =
            this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
          this.alCargarPagina();

          if (this.totalPages > 0 && this.currentPage > this.totalPages) {
            this.consultaPagina(this.totalPages);
            return;
          }

          this.cdr.detectChanges();
        },
        error: () => {
          this.oportunidades = [];
          this.totalRegistros = 0;
          this.totalPages = 0;
          this.cdr.detectChanges();
        },
      });
  }

  mostrarSeguimiento(proyecto: Proyecto): void {
    localStorage.setItem('datoProyecto', JSON.stringify(proyecto));
    this.router.navigate([this.rutaSeguimiento]);
  }

  onProyectoChange(value: string): void {
    this.nomProyecto = value;
    this.consultaPagina(1);
  }

  onClienteChange(value: string): void {
    this.nomCliente = value;
    this.consultaPagina(1);
  }

  onEstatusChange(value: string): void {
    this.estatusOportunidad = value;
    this.consultaPagina(1);
  }

  clearFilters(): void {
    this.nomProyecto = '';
    this.nomCliente = '';
    this.estatusOportunidad = '';
    this.formCliente.setValue('', { emitEvent: false });
    this.consultarClientes();
    this.consultaPagina(1);
  }

  consultarClientes(): void {
    this.clientService.filtroBusquedaCliente(this.requestCliente).subscribe((res) => {
      this.listaClientes = res?.list ?? [];
      this.cdr.detectChanges();
    });
  }

  filtraCliente(): void {
    const search = this.formCliente.value;
    if (!search) {
      this.consultarClientes();
      return;
    }

    this.requestCliente.param1 = String(search).toLowerCase();
    this.clientService.filtroBusquedaCliente(this.requestCliente).subscribe((res) => {
      this.listaClientes = res?.list ?? [];
      this.cdr.detectChanges();
    });
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getHealthLabel(proyecto: Proyecto): string {
    switch (proyecto?.semaforo) {
      case 'success':
        return 'En tiempo';
      case 'warning':
        return 'Por vencer';
      default:
        return 'Fuera de tiempo';
    }
  }

  getHealthClass(proyecto: Proyecto): string {
    switch (proyecto?.semaforo) {
      case 'success':
        return 'is-active';
      case 'warning':
        return 'is-pending';
      default:
        return 'is-danger';
    }
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private decorateProjects(projects: Proyecto[]): Proyecto[] {
    return projects.map((project) => {
      const fechaEstimacion = project.fechaEstimacion ? new Date(project.fechaEstimacion) : null;
      const hoy = new Date();
      let semaforo = 'danger';

      if (fechaEstimacion) {
        const diasRestantes = (fechaEstimacion.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24);
        if (diasRestantes > 30) {
          semaforo = 'success';
        } else if (diasRestantes > 0) {
          semaforo = 'warning';
        }
      }

      return {
        ...project,
        estatus: EstatusProyecto[project.estado?.toString()] ?? project.estatus,
        semaforo,
      };
    });
  }
}
